import type { MenuItem } from '@/core/rbac/types';

/**
 * Custom-event name dispatched by the axios response interceptor on 401.
 * Defined here so both client.ts (sender) and AuthContext (listener) share
 * the same constant without creating a circular import.
 */
export const AUTH_UNAUTHORIZED_EVENT = 'itow:unauthorized' as const;

/**
 * localStorage key for the JWT token.
 * Mirrors the Angular app's key ("tokenItow") for cross-app compatibility
 * during the Angular → React cutover period.
 */
export const TOKEN_STORAGE_KEY = 'tokenItow' as const;

/**
 * Raw JWT payload claims as decoded from the token string.
 * All custom claims arrive as strings even when logically numeric or boolean —
 * that matches the backend JWTConfigurator behaviour.
 *
 * Standard OIDC/JWT claims (sub, jti, iat, exp, iss, aud) are typed with their
 * conventional JS types because the JWT spec defines them as numbers/strings.
 */
export interface JWTClaims {
  /** Numeric user ID, encoded as a string in the JWT. */
  userId: string;
  /** Display name. */
  userName: string;
  /** User email address. Note: the claim key is "mail", not "email". */
  mail: string;
  /** JSON-serialised array of MenuDTO objects. Parse before use. */
  menu: string;
  /** "True" | "False" — indicates single-menu (no role selection) path. */
  singleMenu: string;
  /** "True" | "False" — indicates second-token (multi-role) flow. */
  secondToken: string;
  /** e.g. "Administrator" | "Customer" | "Driver" | "TowProvider" */
  userType: string;
  /** String-encoded source entity ID for driver/provider scoping. */
  sourceTypeId: string;
  /** Optional profile image URL. */
  sourceIMG?: string;
  /** Preferred language, e.g. "en-us" | "es-mx". */
  language: string;

  // Standard JWT claims
  sub: string;
  jti: string;
  iat: number;
  exp: number;
  iss: string;
  aud: string | string[];
}

/**
 * The parsed, strongly-typed view of the authenticated user.
 * Derived from JWTClaims by AuthContext after login / token restore.
 *
 * userId and sourceTypeId remain strings because the JWT encodes them as such;
 * callers that need a numeric form should use Number(userId).
 */
export interface CurrentUser {
  userId: string;
  name: string;
  email: string;
  userType: string;
  sourceTypeId: string;
  language: string;
  menu: MenuItem[];
  singleMenu: boolean;
  secondToken: boolean;
  sourceImage?: string;
}

/** Request body for POST /api/v1/Login/login. */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * `data` field inside the login Response<ResponseLoginDTO> envelope.
 * Mirrors the Angular ILoginResponse and the backend ResponseLoginDTO.
 */
export interface LoginResponseData {
  token: string;
  fullName: string;
  email: string;
  singleMenu: boolean;
  userType: string;
}
